import _each from 'lodash/each';

import Immutable from 'immutable';
import Resource from '../../resource/Resource';

import { currentUserSelector } from '../Users/selectors';
import { updateUser } from '../Users/dispatchers';

import { ActionTypes } from './actions';

import { COLLECTED_ITEM } from '../resourceNames';
import { POST_EXPORT_NAME } from './constants';
import { makeMetricsLogEvent } from '../Metrics/manualEventDispatchers';
import { sendUserpilotTracking } from '../Userpilot/dispatchers';
import { checkFacialIdentity } from '../IdentitySession/dispatchers';
import { updatePost, updatePosts } from './dispatchers.common';

export * from './dispatchers.common';

export const updatePostData = (postData, resolve, reject) => {
  return (dispatch) => {
    const postId = postData.get('id');
    const params = {
      postData: postData.toJS(),
    };

    Resource.update(POST_EXPORT_NAME, postId, 'updatePost', params)
      .done(() => {
        dispatch(updatePost(postData.toJS()));
        resolve();
      })
      .fail(reject);
  };
};

export const updatePortfolioPermissionsForPosts = (
  profileName,
  postIds,
  isPortfolioOn,
  resolve,
  reject
) => {
  return (dispatch, getState) => {
    const params = {
      setPublic: isPortfolioOn,
      postIds,
    };
    const { Posts } = getState();
    const modifiedPosts = Posts.get('byId').map((post, postId) => {
      if (postIds.includes(postId)) {
        return post.set('isPortfolioOn', isPortfolioOn);
      } else {
        return post;
      }
    });

    Resource.execute(
      POST_EXPORT_NAME,
      'updatePortfolioPermissionForPosts',
      params
    )
      .done(() => {
        dispatch(
          makeMetricsLogEvent('Portfolio-Changed', {
            itemChanged: (isPortfolioOn && 'show') || 'hide',
          })
        );

        dispatch({
          type: ActionTypes.PORTFOLIO_POST_SET_MANY,
          posts: modifiedPosts.toList().toJS(),
          profileName,
          postIds,
          isPortfolioOn,
        });
        resolve();
      })
      .fail(reject);
  };
};

export const toggleLikePost = (postId, appWidget = null) => {
  return (dispatch, getState) => {
    const state = getState();
    const currentUser = currentUserSelector(state).get('profileName');
    const post = state.Posts.get('byId').get(postId);
    const likedBy = post.get('likedBy', Immutable.List());
    const likes = post.get('likes');
    const action = !likedBy.includes(currentUser) ? 'like' : 'unlike';
    let newLikedBy = likedBy;
    let newLikes = likes;
    if (action === 'unlike') {
      newLikedBy = likedBy.filter((user) => currentUser !== user);
      newLikes = likes - 1;
    } else {
      newLikedBy = likedBy.push(currentUser);
      newLikes = likes + 1;
    }

    Resource.update(POST_EXPORT_NAME, postId, action)
      .done(() => {
        dispatch(
          makeMetricsLogEvent(
            action === 'like' ? 'UpVote-Made' : 'DownVote-Made',
            {
              onObject: 'Post',
              post: postId,
              appWidget,
            }
          )
        );
      })
      .fail(() => {
        // Revert
        const changes = {
          id: postId,
          likedBy,
          likes,
        };
        dispatch(updatePost(changes));
      });

    const changes = {
      id: postId,
      likedBy: newLikedBy,
      likes: newLikes,
    };
    dispatch(updatePost(changes));
  };
};

export const likePost = (postId) => {
  return (dispatch, getState) => {
    Resource.update(POST_EXPORT_NAME, postId, 'like').done(() => {
      const state = getState();
      const currentUser = currentUserSelector(state).get('profileName');
      const post = state.Posts.getIn(['byId', postId]);
      const likedBy = post.get('likedBy', Immutable.List());

      const changes = {
        id: postId,
        likedBy: likedBy.push(currentUser),
        likes: post.get('likes') + 1,
      };
      dispatch(updatePost(changes));
    });
  };
};

export const unlikePost = (postId) => {
  return (dispatch, getState) => {
    Resource.update(POST_EXPORT_NAME, postId, 'unlike').done(() => {
      const state = getState();
      const currentUser = currentUserSelector(state).get('profileName');
      const post = state.Posts.getIn(['byId', postId]);
      const likedBy = post.get('likedBy', Immutable.List());

      const changes = {
        likedBy: likedBy.filter((user) => currentUser !== user),
        likes: post.get('likes') - 1,
      };
      dispatch(updatePost(changes));
    });
  };
};

export const updatePostNotes = ({ postId, notes, tags, resolve, reject }) => {
  return (dispatch) => {
    const params = {
      notes,
    };

    if (tags instanceof Array) {
      // TODO: make server return "cleaned" tags so we can update post from that
      params.tags = tags;
    }

    Resource.update(POST_EXPORT_NAME, postId, 'updateNotes', params)
      .done(() => {
        params.id = postId;

        dispatch(updatePost(params));
        resolve();
      })
      .fail(reject);
  };
};

export const updateLatestPostForBlock = (
  viewedByUserId,
  blockId,
  type,
  postId
) => {
  // update the store with the latest post ID (per type) for a user
  // on a given block.
  // state.latestPost.blockId.userId.type = postId
  return (dispatch) => {
    dispatch({
      type: ActionTypes.UPDATE_LATEST_POST_FOR_BLOCK,
      viewedByUserId,
      blockId,
      postType: type,
      postId,
    });
  };
};

export const updateLatestPostsForUser = (viewedByUserId, exhibitData) => {
  if (!exhibitData.users && !exhibitData.posts) {
    return () => {};
  }

  return (dispatch) => {
    Object.values(exhibitData.users).forEach((user) => {
      dispatch(updateUser(user));
    });

    _each(exhibitData.posts, (types, blockId) => {
      // Insert/update each post into redux store
      dispatch(updatePosts(Object.values(types)));

      _each(types, (post, type) => {
        dispatch(
          updateLatestPostForBlock(viewedByUserId, blockId, type, post.id)
        );
      });
    });
  };
};

export const sharePost = (args) => {
  return (dispatch, getState) => {
    const { resourceID, type } = args;
    const currentUserId = currentUserSelector(getState()).get('id');

    dispatch(
      makeMetricsLogEvent('share-post', {
        page: resourceID,
        type,
        'user-id': currentUserId,
        'other-data': JSON.stringify(args),
      })
    );

    return new Promise((resolve, reject) => {
      Resource.create(COLLECTED_ITEM, args, ['data'])
        .fail(reject)
        .done(([post], postID) => {
          dispatch(checkFacialIdentity());
          dispatch(updatePosts([post]));
          dispatch(sendUserpilotTracking('Post-Made'));
          resolve({ post, postID });
        });
    });
  };
};
